import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Navigate } from "react-router-dom";
import tw from "twin.macro";
import styled from "styled-components";
import { ReactComponent as SvgDecoratorBlob1 } from "images/svg-decorator-blob-1.svg";
import { ReactComponent as SvgDecoratorBlob2 } from "images/svg-decorator-blob-3.svg";

import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { SectionHeading } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { PrimaryButton } from "components/misc/Buttons.js";

import posts, { getFeaturedPost, searchPosts, getAllTags, countByCategory } from "../data/posts";
import { getCategoryBySlug } from "../data/categories";
import { pluralize } from "../utils/text";
import usePageMeta from "../utils/usePageMeta";
import { SITE_URL, SITE_NAME, BLOG_TITLE, BLOG_DESCRIPTION, POSTS_PER_PAGE, POSTS_PER_LOAD } from "../config";
import { HighlightedText, Eyebrow, TagChip, ArrowLink } from "../components/ui";
import FeaturedPost from "../components/FeaturedPost";
import PostCard from "../components/PostCard";
import CategoryTabs from "../components/CategoryTabs";
import SearchBox from "../components/SearchBox";
import EmptyState from "../components/EmptyState";
import BlogCta from "../components/BlogCta";
import { ReactComponent as ArrowLeftIcon } from "feather-icons/dist/icons/arrow-left.svg";

/**
 * Index du blog : /blog · /blog/categorie/:category · /blog/tag/:tag · /blog?q=recherche
 */

const HeroContainer = tw(Container)``;
// Moins d'air au-dessus du titre (le header suffit), titre et description plus généreux.
const HeroContent = tw(ContentWithPaddingXl)`pt-6 lg:pt-10 pb-8 lg:pb-8`;
const HeadingContainer = tw.div`text-center max-w-4xl mx-auto`;
const Subheading = tw(Eyebrow)`block mb-4 text-base`;
const Heading = tw(SectionHeading)`text-4xl sm:text-5xl lg:text-6xl leading-tight text-primary-900`;
const Description = tw(SectionDescription)`mx-auto text-center max-w-3xl text-base md:text-lg lg:text-xl mt-6`;
const SearchRow = tw.div`mt-10 max-w-2xl mx-auto`;
const TabsRow = tw.div`mt-10 flex justify-center`;
const TagsRow = tw.div`mt-6 flex flex-wrap justify-center`;
const TagsLabel = tw.span`text-xs uppercase font-bold tracking-widest text-gray-600 mr-3 mb-2 mt-1`;
const Tag = tw(TagChip)`mr-2 mb-2`;

const DecoratorBlob1 = tw(SvgDecoratorBlob1)`pointer-events-none -z-10 absolute top-0 right-0 w-64 h-64 opacity-15 transform translate-x-1/2 -translate-y-12 text-primary-500`;
const DecoratorBlob2 = tw(SvgDecoratorBlob2)`pointer-events-none -z-10 absolute bottom-0 left-0 w-64 h-64 opacity-15 transform -translate-x-1/2 translate-y-1/2 text-pink-400`;

const PostsContainer = tw(Container)``;
const PostsContent = tw(ContentWithPaddingXl)`pt-8 lg:pt-8`;
const ResultsRow = tw.div`flex flex-col sm:flex-row sm:items-center sm:justify-between`;
const ResultsCount = tw.p`text-sm font-semibold text-gray-600`;
const ResultsQuery = tw.span`text-gray-900`;
const ResetLink = styled(ArrowLink)`
  ${tw`mt-3 sm:mt-0 text-sm`}
  svg {
    ${tw`ml-0 mr-2`}
  }
  &:hover svg,
  &:focus svg {
    ${tw`-translate-x-1`}
  }
`;
const FeaturedWrap = tw.div`mt-10`;
const Grid = tw.div`mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3`;
const ButtonContainer = tw.div`flex justify-center`;
const LoadMoreButton = tw(PrimaryButton)`mt-16 mx-auto px-10 py-4`;

const headingWithHighlight = label => {
  const words = String(label).trim().split(" ");
  if (words.length === 1) return <HighlightedText>{label}</HighlightedText>;
  const last = words.pop();
  return (
    <>
      {words.join(" ")} <HighlightedText>{last}</HighlightedText>
    </>
  );
};

export default function BlogIndex() {
  const { category: categorySlug, tag: tagSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get("q") || "";
  const [query, setQuery] = useState(urlQuery);
  const [visible, setVisible] = useState(POSTS_PER_PAGE);

  // Synchronise l'URL (?q=) avec le champ, avec un léger délai pour ne pas réécrire l'historique à chaque touche.
  useEffect(() => {
    setQuery(urlQuery);
  }, [urlQuery]);
  useEffect(() => {
    if (query === urlQuery) return undefined;
    const t = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (query.trim()) next.set("q", query.trim());
      else next.delete("q");
      setSearchParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [query, urlQuery, searchParams, setSearchParams]);

  const category = categorySlug ? getCategoryBySlug(categorySlug) : null;
  const allTags = useMemo(() => getAllTags(), []);
  const tag = tagSlug ? allTags.find(t => t.slug === tagSlug) || null : null;
  const counts = useMemo(() => countByCategory(), []);

  const filterKey = `${categorySlug || ""}|${tagSlug || ""}|${urlQuery}`;
  useEffect(() => {
    setVisible(POSTS_PER_PAGE);
  }, [filterKey]);

  const isFiltered = Boolean(category || tagSlug || urlQuery.trim());
  const base = useMemo(() => {
    let list = posts;
    if (category) list = list.filter(p => p.category.id === category.id);
    if (tagSlug) list = list.filter(p => p.tagSlugs.includes(tagSlug));
    return list;
  }, [category, tagSlug]);
  const results = useMemo(() => searchPosts(urlQuery, base), [urlQuery, base]);

  const featured = !isFiltered ? getFeaturedPost() : null;
  const gridPosts = featured ? results.filter(p => p.slug !== featured.slug) : results;
  const shown = gridPosts.slice(0, visible);

  // Métadonnées de la page.
  const pageTitle = category ? `${category.label} — Blog` : tag ? `${tag.tag} — Blog` : urlQuery ? `Recherche : ${urlQuery} — Blog` : BLOG_TITLE;
  const pageDescription = category ? category.description : tag ? `Tous les articles sur ${tag.tag} : IA, tech et innovation à Besançon.` : BLOG_DESCRIPTION;
  const pagePath = category ? `/blog/categorie/${category.slug}` : tagSlug ? `/blog/tag/${tagSlug}` : "/blog";
  usePageMeta({
    title: pageTitle,
    description: pageDescription,
    path: pagePath,
    noindex: Boolean(urlQuery.trim()),
    jsonLd: {
      "@context": "https://schema.org",
      "@type": category || tag ? "CollectionPage" : "Blog",
      name: pageTitle,
      description: pageDescription,
      url: `${SITE_URL}${pagePath}`,
      inLanguage: "fr-FR",
      publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    },
  });

  // Une catégorie ou un tag inexistant renverrait sinon une page indexable au contenu vide :
  // autant d'URL sans valeur que Google classerait en « soft 404 ». On redirige vers l'index.
  if (categorySlug && !category) return <Navigate to="/blog" replace />;
  if (tagSlug && !tag) return <Navigate to="/blog" replace />;

  const heading = category ? (
    headingWithHighlight(category.label)
  ) : tag ? (
    <>
      Sujet : <HighlightedText>{tag.tag}</HighlightedText>
    </>
  ) : (
    <>
      Les nouveautés tech à <HighlightedText>Besançon.</HighlightedText>
    </>
  );
  const description = category ? category.description : tag ? `Tous les articles qui parlent de ${tag.tag}.` : BLOG_DESCRIPTION;

  return (
    <AnimationRevealPage>
      <HeroContainer>
        <HeroContent>
          <HeadingContainer>
            <Subheading>{category ? "Catégorie" : tag ? "Sujet" : "Le blog"}</Subheading>
            <Heading as="h1">{heading}</Heading>
            <Description>{description}</Description>
          </HeadingContainer>
          <SearchRow>
            <SearchBox value={query} onChange={setQuery} />
          </SearchRow>
          <TabsRow>
            <CategoryTabs activeId={category ? category.id : null} counts={counts} total={posts.length} />
          </TabsRow>
          {allTags.length > 0 && (
            <TagsRow aria-label="Sujets populaires">
              <TagsLabel>Sujets</TagsLabel>
              {allTags.slice(0, 12).map(t => (
                <Tag key={t.slug} to={`/blog/tag/${t.slug}`} aria-current={t.slug === tagSlug ? "page" : undefined}>
                  {t.tag}
                </Tag>
              ))}
            </TagsRow>
          )}
        </HeroContent>
        <DecoratorBlob1 />
        <DecoratorBlob2 />
      </HeroContainer>

      <PostsContainer>
        <PostsContent>
          <ResultsRow>
            <ResultsCount aria-live="polite">
              {urlQuery.trim() ? (
                <>
                  {pluralize(results.length, "résultat")} pour <ResultsQuery>« {urlQuery.trim()} »</ResultsQuery>
                </>
              ) : (
                pluralize(results.length, "article")
              )}
              {category && !urlQuery.trim() && <> dans {category.label}</>}
              {tag && !urlQuery.trim() && <> sur {tag.tag}</>}
            </ResultsCount>
            {isFiltered && (
              <ResetLink to="/blog">
                <ArrowLeftIcon aria-hidden="true" />
                Tous les articles
              </ResetLink>
            )}
          </ResultsRow>

          {featured && (
            <FeaturedWrap>
              <FeaturedPost post={featured} />
            </FeaturedWrap>
          )}

          {results.length === 0 ? (
            <EmptyState
              title={urlQuery.trim() ? "Aucun article ne correspond à votre recherche" : "Pas encore d'article ici"}
              text={urlQuery.trim() ? "Essayez d'autres mots-clés, ou parcourez les catégories." : "De nouveaux articles arrivent régulièrement. En attendant, parcourez les autres catégories."}
            />
          ) : (
            shown.length > 0 && (
              <Grid>
                {shown.map(post => (
                  <PostCard key={post.slug} post={post} />
                ))}
              </Grid>
            )
          )}

          {visible < gridPosts.length && (
            <ButtonContainer>
              <LoadMoreButton type="button" onClick={() => setVisible(v => v + POSTS_PER_LOAD)}>
                Charger plus d'articles
              </LoadMoreButton>
            </ButtonContainer>
          )}
        </PostsContent>
      </PostsContainer>

      <BlogCta />
    </AnimationRevealPage>
  );
}
